import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderCXMLDtoService } from './order-cxml-dto.service';
import {
  PanAppState,
  PlatformService,
  UsfTokenStorageService,
} from '@panamax/app-state';
import {
  Order,
  OrderPunchoutStoreService,
  partnerSelectors,
} from '@usf/ngrx-order';
import { Router } from '@angular/router';
import { UserService } from '@app/user/services';
import { take } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { PUNCHOUT_TRACING_ACTIONS } from '@shared/tracking/tracing/punchout/punchout-tracing.actions';
import { transmitOrderTrace } from '@order/tracking/tracing/trace-models/transmit-order.trace';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { ORDER_TRACING_ACTIONS } from '@order/tracking/tracing/order-tracing.actions';
import { LoadingSpinnerWMessageModalComponent } from '@shared/components/loading-spinner-w-message-modal/loading-spinner-w-message-modal.component';
import { ModalController } from '@ionic/angular';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { PATHS } from '@shared/constants/paths';

@Injectable({
  providedIn: 'root',
})
export class OrderPunchoutService {
  loadingSpinnerModal: any;
  constructor(
    readonly router: Router,
    private store: Store,
    private tokenStorageService: UsfTokenStorageService,
    private userService: UserService,
    private panAppState: PanAppState,
    private modalController: ModalController,
    private platformService: PlatformService,
    private orderPunchoutStoreService: OrderPunchoutStoreService,
    private messageStoreService: MessageStoreService,
  ) {}
  loadingSpinnerModalWMessage: HTMLIonModalElement;

  doPunchoutTransfer(orders: Order[]) {
    combineLatest([
      this.tokenStorageService.getPunchoutSession(),
      this.userService.userCustomizations$(),
      this.panAppState.user$,
      this.store.select(productWithAlternativesSelector),
      this.store.select(partnerSelectors.selectPartner),
    ])
      .pipe(take(1))
      .subscribe(
        ([
          punchoutSession,
          userCusomizations,
          user,
          productMap,
          partnerState,
        ]) => {
          const buyerCookie = punchoutSession.buyerCookie;
          const browserFormPost = punchoutSession.browserFormPost;
          const userId = user.userId;
          const orderXML = OrderCXMLDtoService.orderToCXMLFormat(
            orders,
            productMap,
            partnerState,
            buyerCookie,
            browserFormPost,
            userId,
            userCusomizations,
          );

          // Build trace object
          const tracking = JSON.parse(JSON.stringify(transmitOrderTrace));
          tracking.tracing.data.isStartOfTrace = true;
          tracking.tracing.data.isEndOfTrace = true;
          tracking.tracing.transformFunc = genericTracingTransformer;
          tracking.tracing.data.attributes.order.punchOutCxml = orderXML;
          if (!!orders[0]) {
            const firstOrder = orders[0];
            tracking.tracing.data.attributes.order.orderId =
              firstOrder.orderHeader?.orderId;
            tracking.tracing.data.attributes.order.origOrderId =
              firstOrder.orderHeader?.origOrderId;
            tracking.tracing.data.attributes.order.origSplitOrderId =
              firstOrder.orderHeader?.origSplitOrderId;
            tracking.tracing.data.attributes.order.totalAmount =
              firstOrder.orderHeader?.totalPrice;
            tracking.tracing.data.attributes.order.requestedDeliveryDate =
              firstOrder.orderHeader?.requestedDeliveryDate;
            tracking.tracing.data.attributes.order.confirmedDeliveryDate =
              firstOrder.orderHeader?.confirmedDeliveryDate;
            tracking.tracing.data.attributes.order.tandemOrderNumber =
              firstOrder.orderHeader?.tandemOrderNumber;
            tracking.tracing.data.attributes.order.importOrderId =
              firstOrder.orderHeader?.importOrderIdentifier;
            tracking.tracing.data.attributes.order.importedOrdersCount =
              orders.length;
          }
          // Dispatch the event for transmit order trace
          this.store.dispatch(
            ORDER_TRACING_ACTIONS.traceTransmitOrderFromClient({ tracking }),
          );

          localStorage.setItem('browserFormPost', browserFormPost);
          localStorage.setItem('orderXML', orderXML);
          this.orderPunchoutStoreService.punchoutLogout(orderXML);
        },
      );
  }
  async createSpinnerWithMessageModal() {
    this.loadingSpinnerModalWMessage = await this.modalController.create({
      component: LoadingSpinnerWMessageModalComponent,
      backdropDismiss: false,
      cssClass:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? 'ion-modal-touch'
          : 'modal-desktop-size-lg',
      initialBreakpoint:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? 1
          : undefined,
      breakpoints:
        this.platformService.platformType !==
        this.platformService.platformEnum.desktop
          ? [0, 1]
          : undefined,
    });
    return this.loadingSpinnerModalWMessage?.present();
  }
  async dismissSpinnerModal() {
    return this.loadingSpinnerModalWMessage?.dismiss();
  }

  tracePunchoutTransferClick() {
    this.store.dispatch(PUNCHOUT_TRACING_ACTIONS.transfer());
  }

  showCartTransferFailedAlert() {
    this.messageStoreService.upsertMessage({
      stack: null,
      watermark: new Date().toUTCString(),
      read: false,
      type: MessageTypeEnum.error,
      display: 'i18n.cartTransferFailureModal.message',
      dismissRoute: [PATHS.LOGOUT],
    } as Message);
  }
}
